import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import RelationListLayout from '../list/layout/RelationListLayout';
// components
import SensitiveDataPlug from './SensitiveDataPlug';
import ToggleSensitiveDataButton from './buttons/ToggleSensitiveDataButton';
// services
import { useListContext } from 'framework/list';
import useSensitiveDataService from './sensitiveDataService';
import { SensitiveDataContextProvider } from './SensitiveDataContext';
import type { ToggleSensitiveDataButtonProps } from './buttons/ToggleSensitiveDataButton';

interface SensitiveRelationListPageLayoutProps {
  actions?: React.ReactElement[];
  children?: React.ReactElement;
  sensitiveDataIdSource: string;
  loadSensitiveDataMethod: (ids: any) => Promise<Dictionary<any>>;
  timerId?: string;
  toggleSensitiveDataButtonProps?: Partial<ToggleSensitiveDataButtonProps>;
  [key: string]: any;
}

const SensitiveRelationListPageLayout: React.FC<SensitiveRelationListPageLayoutProps> = ({
  actions = [],
  children = null,
  sensitiveDataIdSource,
  loadSensitiveDataMethod,
  timerId = '',
  toggleSensitiveDataButtonProps,
  ...other
}) => {
  const [sensitiveDataIds, setSensitiveDataIds] = useState([]);
  const { data: { items = [] } = {} } = useListContext();

  const {
    isTimerActive,
    isShown,
    toggleSensitiveData,
    getValueByIdAndSource,
    isSensitiveDataLoading,
    loadSensitiveDataByIds,
    sensitiveData,
  } = useSensitiveDataService({
    loadSensitiveDataMethod,
    timerId,
  });

  useEffect(() => {
    if (items.length) {
      const sensitiveIds = items.map((compensation: any) => compensation[sensitiveDataIdSource]);

      setSensitiveDataIds(sensitiveIds as any);
    }
  }, [items, sensitiveDataIdSource]);

  const hasSensitiveData = useMemo(() => sensitiveData && Object.keys(sensitiveData).length, [sensitiveData]);

  const isShownWithoutSensitiveData = useMemo(
    () => sensitiveDataIds?.length && !hasSensitiveData && isShown,
    [isShown, hasSensitiveData, sensitiveDataIds],
  );

  useEffect(() => {
    if (isShownWithoutSensitiveData) {
      loadSensitiveDataByIds(sensitiveDataIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShownWithoutSensitiveData]);

  const getActions = () => {
    const buttons = [...actions];
    const toggleButton = (
      <ToggleSensitiveDataButton
        key="toggle-sensitive-data"
        isShown={isShown}
        onClick={() => toggleSensitiveData(sensitiveDataIds)}
        disabled={isSensitiveDataLoading}
        isTimerActive={isTimerActive}
        {...toggleSensitiveDataButtonProps}
      />
    );

    if (items.length) {
      buttons.unshift(toggleButton);
    }

    return buttons;
  };

  return (
    <SensitiveDataContextProvider
      value={{
        sensitiveDataIdSource,
        getValueByIdAndSource,
      }}
    >
      <RelationListLayout actions={getActions()} {...other}>
        {
          ((isShown && !isSensitiveDataLoading && hasSensitiveData) || !items.length ? (
            children
          ) : (
            <SensitiveDataPlug />
          )) as ReactElement
        }
      </RelationListLayout>
    </SensitiveDataContextProvider>
  );
};

export default SensitiveRelationListPageLayout;
